/**
 * Tipos e labels do módulo Client Onboarding (frontend).
 *
 * Espelha o enum `OnboardingStage`, `OnboardingEventType` e
 * `OnboardingActorType` do Prisma (apps/api/prisma/schema.prisma).
 * Manter em sincronia manualmente: caso um enum novo seja adicionado
 * no backend, adicione aqui também.
 */

// ------------------------------------------------------------------
// Enums (espelhados do Prisma)
// ------------------------------------------------------------------

export const OnboardingStage = {
  AWAITING_FORM_1: 'AWAITING_FORM_1',
  FORM_1_COMPLETED: 'FORM_1_COMPLETED',
  AWAITING_FORM_2: 'AWAITING_FORM_2',
  FORM_2_COMPLETED: 'FORM_2_COMPLETED',
  AWAITING_CALL_SCHEDULE_TRIGGER: 'AWAITING_CALL_SCHEDULE_TRIGGER',
  CALL_LINK_SENT: 'CALL_LINK_SENT',
  CALL_SCHEDULED: 'CALL_SCHEDULED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;
export type OnboardingStage = (typeof OnboardingStage)[keyof typeof OnboardingStage];

export const OnboardingEventType = {
  CASE_CREATED: 'CASE_CREATED',
  FORM_1_SUBMITTED: 'FORM_1_SUBMITTED',
  FORM_2_SUBMITTED: 'FORM_2_SUBMITTED',
  SEND_CALL_LINK_CLICKED: 'SEND_CALL_LINK_CLICKED',
  CALL_SCHEDULED: 'CALL_SCHEDULED',
  STAGE_ADVANCED: 'STAGE_ADVANCED',
  MESSAGE_SENT_CLIENT: 'MESSAGE_SENT_CLIENT',
  MESSAGE_SENT_TEAM: 'MESSAGE_SENT_TEAM',
  AI_ANALYSIS_COMPLETED: 'AI_ANALYSIS_COMPLETED',
  AI_ANALYSIS_FAILED: 'AI_ANALYSIS_FAILED',
  AI_ANALYSIS_RETRIED: 'AI_ANALYSIS_RETRIED',
  WHATSAPP_GROUP_CREATED: 'WHATSAPP_GROUP_CREATED',
  NOTION_UPDATED: 'NOTION_UPDATED',
  DASHBOARD_USER_CREATED: 'DASHBOARD_USER_CREATED',
  MANUAL_ACTION: 'MANUAL_ACTION',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
} as const;
export type OnboardingEventType = (typeof OnboardingEventType)[keyof typeof OnboardingEventType];

export const OnboardingActorType = {
  SYSTEM: 'SYSTEM',
  USER: 'USER',
  WEBHOOK: 'WEBHOOK',
} as const;
export type OnboardingActorType = (typeof OnboardingActorType)[keyof typeof OnboardingActorType];

// ------------------------------------------------------------------
// Interfaces principais
// ------------------------------------------------------------------

export interface OnboardingCase {
  id: string;
  // Identificação do cliente
  notionPageId: string;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  clientCpf: string | null;
  clientBirthDate: string | null; // ISO date
  // Estado atual
  currentStage: OnboardingStage;
  failedAt: string | null;
  failureReason: string | null;
  // Dados coletados/gerados
  aiAnalysis: string | null;
  aiAnalysisAt: string | null;
  aiAnalysisError: string | null;
  form1Summary: string | null;
  form1CompletedAt: string | null;
  form2Summary: string | null;
  form2CompletedAt: string | null;
  callLinkSentAt: string | null;
  callSchedulingToken: string | null;
  callScheduledAt: string | null;
  completedAt: string | null;
  // Grupo WhatsApp (só criado na etapa 04)
  whatsappGroupId: string | null;
  whatsappGroupInvite: string | null;
  clientJoinedGroup: boolean | null;
  // Dashboard user
  dashboardUserCreated: boolean;
  dashboardUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingEvent {
  id: string;
  caseId: string;
  type: OnboardingEventType;
  stageBefore: OnboardingStage | null;
  stageAfter: OnboardingStage | null;
  executionId: string | null;
  actorType: OnboardingActorType;
  actorId: string | null;
  message: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export interface OnboardingCaseWithEvents extends OnboardingCase {
  events: OnboardingEvent[];
}

// ------------------------------------------------------------------
// Respostas de API (envelopes)
// ------------------------------------------------------------------

export interface ListCasesResponse {
  cases: OnboardingCase[];
  total: number;
  skip: number;
  take: number;
}

export interface StatsResponse {
  total: number;
  byStage: Record<OnboardingStage, number>;
}

export interface RetryAiAnalysisResponse {
  ok: boolean;
  aiAnalysis: string | null;
  error?: string;
  reason?: 'already_has_analysis' | 'no_form_summary' | 'ai_not_configured';
}

// ------------------------------------------------------------------
// Labels & helpers de UI
// ------------------------------------------------------------------

export const STAGE_LABELS: Record<OnboardingStage, string> = {
  AWAITING_FORM_1: 'Aguardando Form 1',
  FORM_1_COMPLETED: 'Form 1 concluído',
  AWAITING_FORM_2: 'Aguardando Form 2',
  FORM_2_COMPLETED: 'Form 2 concluído',
  AWAITING_CALL_SCHEDULE_TRIGGER: 'Aguardando envio do link',
  CALL_LINK_SENT: 'Link de call enviado',
  CALL_SCHEDULED: 'Call agendada',
  COMPLETED: 'Onboarding concluído',
  FAILED: 'Falha',
};

export const STAGE_ORDER: OnboardingStage[] = [
  'AWAITING_FORM_1',
  'FORM_1_COMPLETED',
  'AWAITING_FORM_2',
  'FORM_2_COMPLETED',
  'AWAITING_CALL_SCHEDULE_TRIGGER',
  'CALL_LINK_SENT',
  'CALL_SCHEDULED',
  'COMPLETED',
  'FAILED',
];

/**
 * Tailwind color classes por stage — usadas em badges/pills.
 */
export const STAGE_COLORS: Record<OnboardingStage, string> = {
  AWAITING_FORM_1: 'bg-gray-100 text-gray-700 border-gray-300',
  FORM_1_COMPLETED: 'bg-blue-100 text-blue-700 border-blue-300',
  AWAITING_FORM_2: 'bg-amber-100 text-amber-700 border-amber-300',
  FORM_2_COMPLETED: 'bg-indigo-100 text-indigo-700 border-indigo-300',
  AWAITING_CALL_SCHEDULE_TRIGGER: 'bg-purple-100 text-purple-700 border-purple-300',
  CALL_LINK_SENT: 'bg-cyan-100 text-cyan-700 border-cyan-300',
  CALL_SCHEDULED: 'bg-teal-100 text-teal-700 border-teal-300',
  COMPLETED: 'bg-green-100 text-green-700 border-green-300',
  FAILED: 'bg-red-100 text-red-700 border-red-300',
};

export const EVENT_LABELS: Record<OnboardingEventType, string> = {
  CASE_CREATED: 'Caso criado',
  FORM_1_SUBMITTED: 'Form 1 recebido',
  FORM_2_SUBMITTED: 'Form 2 recebido',
  SEND_CALL_LINK_CLICKED: 'Botão "enviar link" acionado',
  CALL_SCHEDULED: 'Call agendada',
  STAGE_ADVANCED: 'Etapa avançada',
  MESSAGE_SENT_CLIENT: 'Mensagem enviada ao cliente',
  MESSAGE_SENT_TEAM: 'Time notificado',
  AI_ANALYSIS_COMPLETED: 'Análise IA concluída',
  AI_ANALYSIS_FAILED: 'Análise IA falhou',
  AI_ANALYSIS_RETRIED: 'Análise IA reprocessada',
  WHATSAPP_GROUP_CREATED: 'Grupo WhatsApp criado',
  NOTION_UPDATED: 'Notion atualizado',
  DASHBOARD_USER_CREATED: 'Usuário da dashboard criado',
  MANUAL_ACTION: 'Ação manual',
  WARNING: 'Aviso',
  ERROR: 'Erro',
};
